/* eslint-disable no-console */
/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import {
  ArchivedState,
  CustomListTeamMembersQuery,
  Position, SearchableTeamMemberFilterInput, SearchTeamMemberNamesQuery, SearchTeamMembersQuery, TeamMember,
} from 'API';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  customListTeamMembers, customSearchTeamMembers, listCaseTeamCandidates as listCaseTeamCandidatesQuery, listFirmContacts, searchTeamMemberNames,
} from 'graphql/customQueries';
import * as mutations from 'graphql/mutations';
import { searchTeamMembers } from 'graphql/queries';
import { sleep } from 'shared/constants';
import { fetchListAll, getCacheWithExpiration, lower } from 'shared/utilities';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

class TeamService {
  /**
   * Make TeamMember a firm admin.
   * @param teamMember - The Dynamo ID of the team member.
   * @param teamMemberUsername - The Cognito ID of the team member.
   */
  addAdministrator = async (
    teamMemberID: string,
    teamMemberUsername?: string | null,
  ) => {
    try {
      await client.graphql({
        query: mutations.updateTeamMember,
        variables: {
          input: {
            id: teamMemberID,
            isAdmin: true,
          },
        },
      });

      if (teamMemberUsername) {
        const payload = {
          body: {
            username: teamMemberUsername,
            groupname: 'Admins',
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: (await fetchAuthSession()).tokens?.accessToken?.toString() ?? '',
          },
        };

        await (await post({ apiName: 'AdminQueries', path: '/addUserToGroup', options: payload }).response).body;
      }
    } catch (err) {
      throw new Error();
    }
  };

  changeManager = async (teamMemberID: string, managerID: string | null) => {
    try {
      await client.graphql({
        query: mutations.updateTeamMember,
        variables: {
          input: {
            id: teamMemberID,
            managerID,
          },
        },
      });
    } catch (err) {
      throw new Error();
    }
  };

  changePosition = async (teamMemberID: string, position: Position | null) => {
    try {
      await client.graphql({
        query: mutations.updateTeamMember,
        variables: {
          input: {
            id: teamMemberID,
            position,
          },
        },
      });
    } catch (err) {
      throw new Error();
    }
  };

  listTeamMembers = async (): Promise<Array<TeamMember>> => getCacheWithExpiration(
    this.teamCacheKey,
    async () => fetchListAll(listFirmContacts),
    10 * 1000,
  );

  listCaseTeamCandidates = async (): Promise<Array<TeamMember>> => getCacheWithExpiration(
    'team-case-candidates',
    async () => fetchListAll(listCaseTeamCandidatesQuery),
    10 * 1000,
  );

  /**
   * Remove admin status from a team member
   * @param teamMemberID - The Dynamo ID of the team member.
   * @param teamMemberUsername - The Cognito ID of the team mamber.
   */
  removeAdministrator = async (
    teamMemberID: string,
    teamMemberUsername?: string | null,
  ) => {
    try {
      await client.graphql({
        query: mutations.updateTeamMember,
        variables: {
          input: {
            id: teamMemberID,
            isAdmin: false,
          },
        },
      });

      if (teamMemberUsername) {
        const payload = {
          body: {
            username: teamMemberUsername,
            groupname: 'Admins',
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: (await fetchAuthSession()).tokens?.accessToken?.toString() ?? '',
          },
        };

        await (await post({ apiName: 'AdminQueries', path: '/removeUserFromGroup', options: payload }).response).body;
      }
    } catch (err) {
      throw new Error();
    }
  };

  searchByName: SearchByName<TeamMember> = async (
    name?: string,
    maxCount?: number,
    archived?: ArchivedState,
  ) => {
    if (!name) {
      return [];
    }

    const chunks = lower(name).split(' ');

    const andArray: Array<SearchableTeamMemberFilterInput> = [
      {
        archived: {
          eq: archived || 'ACTIVE',
        },
      },
    ];

    chunks.forEach((chunk) => {
      andArray.push({ lowerFull: { wildcard: `*${chunk}*` } });
    });

    try {
      const response = (await client.graphql({
        query: searchTeamMemberNames,
        variables: {
          and: andArray,
          limit: maxCount,
        },
      })) as GraphQLResult<SearchTeamMemberNamesQuery>;

      return (response.data?.searchTeamMembers?.items
        || []) as Array<TeamMember>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  public fetchTeamMemberByIds = async (ids: any) => {
    try {
      const condition: { or: [SearchableTeamMemberFilterInput] } = {
        or: [{}],
      };

      ids?.map((id: string) => {
        condition.or.push({ id: { eq: id } });
      });

      const response = (await client.graphql({
        query: searchTeamMembers,
        variables: {
          filter: condition,
          limit: 300,
        },
      })) as GraphQLResult<SearchTeamMembersQuery>;

      return (response.data?.searchTeamMembers?.items
        || []) as Array<TeamMember>;
    } catch (error) {
      console.log('ERROR >>>>>>>', error);
    }
  };

  public fetchTeammembers = async () => {
    let teammembers: TeamMember[] = [];
    let nextToken = null;

    do {
      // eslint-disable-next-line no-await-in-loop
      const response = await client.graphql({
        query: customListTeamMembers,
        variables: {
          limit: 300,
          nextToken,
        },
      }) as GraphQLResult<CustomListTeamMembersQuery>;

      // eslint-disable-next-line no-await-in-loop
      await sleep(1000);

      const { items, nextToken: token } = response?.data?.listTeamMembers || {};
      if (items?.length) {
        teammembers = [...teammembers, ...items] as TeamMember[];
      }
      nextToken = token;
    } while (nextToken !== null);

    return teammembers;
  };

  searchTeamMemberForReport = async (
    filter?: SearchableTeamMemberFilterInput,
    limit?: Number,
    token?: String,
  ) => {
    try {
      const response = (await client.graphql({
        query: customSearchTeamMembers,
        variables: {
          filter,
          limit,
          nextToken: token,
        },
      })) as GraphQLResult<SearchTeamMembersQuery>;

      return response.data?.searchTeamMembers?.items as Array<TeamMember>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  teamCacheKey = 'team-firm';
}

export default new TeamService();
